// R115 C1 (Jesse: "task mentions → in-app notification for the tagged person —
// 'someone mentioned you' / 'task updated'"): the bell in the TopNav right
// cluster. Reads the caller's own tiny per-user store (GET
// /api/tasks/notifications/mine — never the 240 KB tasks blob) and lists
// mentions, assignments, updates and comments, newest first.
//
// Lifecycle:
//   notifBellHtml()   markup for TopNav.render() ('' for owner / accounting —
//                     the InfoAskPill role gate). Drawn from the module cache,
//                     so a nav re-render (every navigation) keeps the badge.
//   bindNotifBell()   TopNav.bindEvents(): wires the freshly rendered bell and,
//                     on a landing that never reaches App's mount line (a Slack
//                     deep link), starts the poll a few seconds later.
//   mountNotifBell()  App, after the first page load: poll every 60 s while the
//                     tab is visible, immediately when it becomes visible again,
//                     and ~1.5 s after any task write (TasksPage dispatches
//                     'tasks:changed'; the server pushes after its response).
// A click opens the task: sessionStorage 'tk_focus_task' (the page's existing
// one-shot hand-off) + 'tasks:focus' (TasksPage already on screen) +
// app:navigate 'tasks'. Styles: main.css tokens only, injected once.

type Notif = {
  id: string; at: string; kind: 'mention' | 'assigned' | 'updated' | 'comment' | string;
  task_id: string; title: string; listing_name?: string; by: string; by_name?: string;
  text?: string; read: boolean; deleted?: boolean;
};

const API = '/api/tasks/notifications';
const POLL_MS = 60 * 1000;
const AFTER_WRITE_MS = 1500;
const SHOW_MAX = 30;

let items: Notif[] = [];
let unread = 0;
let loaded = false;
let loadFailed = false;   // the last fetch failed before anything ever loaded
let lastOkAt = 0;
let sig = '';
let started = false;
let pollTimer: number | undefined;
let soonTimer: number | undefined;
let startTimer: number | undefined;
let inflight: Promise<void> | null = null;
let docBound = false;

const esc = (s: unknown) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const role = (): string => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } };
export function bellAllowed(): boolean { const r = role(); return r !== 'owner' && r !== 'accounting'; }

const BELL_SVG = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';

const KIND: Record<string, [string, string]> = {
  mention: ['Mentioned you', 'pill-blue'],
  assigned: ['Assigned', 'pill-green'],
  updated: ['Updated', 'pill-amber'],
  comment: ['Comment', 'pill-gray'],
};

function relTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function who(n: Notif): string {
  if (n.by === 'system') return n.by_name || 'Reservation sync';
  return n.by_name || (n.by ? n.by.split('@')[0] : 'Someone');
}
function lineFor(n: Notif): string {
  const t = String(n.text || '');
  if (n.deleted) return `${esc(who(n))} deleted this task`;
  if (n.kind === 'mention') return `${esc(who(n))} mentioned you${t ? `: “${esc(t)}”` : ''}`;
  if (n.kind === 'comment') return `${esc(who(n))} commented${t ? `: “${esc(t)}”` : ''}`;
  if (n.kind === 'assigned') return `${esc(who(n))} ${esc(t || 'assigned you')}`;
  return `${esc(who(n))} · ${esc(t || 'updated the task')}`;
}

function badgeText(): string { return unread > 9 ? '9+' : String(unread); }
function listHtml(): string {
  if (!items.length) {
    return loaded
      ? `<div class="tn-notif-empty">You're all caught up. Mentions, assignments and changes to your tasks land here.</div>`
      : `<div class="tn-notif-empty">${loadFailed ? 'Couldn’t load notifications just now — retrying every minute.' : 'Loading…'}</div>`;
  }
  return items.slice(0, SHOW_MAX).map(n => {
    const [label, pill] = n.deleted ? ['Deleted', 'pill-red'] : (KIND[n.kind] || ['Update', 'pill-gray']);
    return `<button type="button" class="tn-notif-item ${n.read ? '' : 'unread'}" data-notif-id="${esc(n.id)}" role="menuitem">
      <span class="tn-notif-dot" aria-hidden="true"></span>
      <span class="tn-notif-body">
        <span class="tn-notif-top"><b class="tn-notif-title">${esc(n.title || 'Task')}</b><span class="pill ${pill}">${label}</span></span>
        <span class="tn-notif-text">${lineFor(n)}</span>
        <span class="tn-notif-meta">${n.listing_name ? `${esc(n.listing_name)} · ` : ''}${esc(relTime(n.at))}</span>
      </span>
    </button>`;
  }).join('');
}
function menuInnerHtml(): string {
  return `
    <div class="tn-notif-head">
      <span class="tn-notif-h">Notifications${unread ? ` <span class="tn-notif-hn">${unread} new</span>` : ''}</span>
      <button type="button" class="tn-notif-act" data-notif-all ${unread ? '' : 'disabled'}>Mark all read</button>
    </div>
    <div class="tn-notif-list">${listHtml()}</div>
    <div class="tn-notif-foot"><button type="button" class="tn-notif-act" data-notif-open-tasks>Open tasks →</button></div>`;
}

// TopNav markup — between the refresh button and the account chip.
export function notifBellHtml(): string {
  if (!bellAllowed()) return '';
  ensureStyles();
  return `
    <div class="tn-drop tn-drop-right tn-notif" id="tn-notif">
      <button type="button" class="tn-icon tn-notif-btn" id="notif-btn" aria-haspopup="true" aria-expanded="false" aria-label="${unread ? `Notifications, ${unread} unread` : 'Notifications'}" title="Task notifications">${BELL_SVG}<span class="tn-notif-n" ${unread ? '' : 'hidden'}>${badgeText()}</span></button>
      <div class="tn-menu tn-notif-menu" role="menu" aria-label="Task notifications">${menuInnerHtml()}</div>
    </div>`;
}

function paint(): void {
  const root = document.getElementById('tn-notif');
  if (!root) return;
  const btn = root.querySelector<HTMLElement>('#notif-btn');
  const n = root.querySelector<HTMLElement>('.tn-notif-n');
  if (n) { n.hidden = !unread; n.textContent = badgeText(); }
  if (btn) btn.setAttribute('aria-label', unread ? `Notifications, ${unread} unread` : 'Notifications');
  const menu = root.querySelector<HTMLElement>('.tn-notif-menu');
  if (menu) menu.innerHTML = menuInnerHtml();
}
function setItems(next: Notif[], nextUnread: number): void {
  items = Array.isArray(next) ? next : [];
  unread = Number.isFinite(nextUnread) ? nextUnread : items.filter(i => !i.read).length;
  const s = `${unread}|${items.map(i => `${i.id}:${i.read ? 1 : 0}:${i.at}`).join(',')}`;
  if (s === sig) return;   // diff-only repaint (PhotoQueuePill idiom) — an open menu isn't rebuilt under the finger
  sig = s;
  paint();
}

export function refreshNotifications(): Promise<void> {
  if (!bellAllowed()) return Promise.resolve();
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await fetch(`${API}/mine`);
      if (!r.ok) { if (!loaded && !loadFailed) { loadFailed = true; paint(); } return; }   // 503 on a failed read: keep the last state, retry next tick
      const j = await r.json();
      loaded = true; lastOkAt = Date.now();
      setItems(Array.isArray(j.items) ? j.items : [], Number(j.unread));
    } catch { if (!loaded && !loadFailed) { loadFailed = true; paint(); } /* offline — keep the last state */ }
    finally { inflight = null; }
  })();
  return inflight;
}
function refreshSoon(ms = AFTER_WRITE_MS): void {
  if (soonTimer) window.clearTimeout(soonTimer);
  soonTimer = window.setTimeout(() => { soonTimer = undefined; void refreshNotifications(); }, ms);
}
// R115 fix (review tasks 2): send exactly what this bell showed — each item's
// id with the `at` it showed. The server marks an item read only if it is
// still that version, so a mention that landed after the last poll (or an
// 'updated' burst folded into a shown item) stays unread; the reconcile below
// then sees the server's unread > 0 and re-polls, and the badge lights up.
function markRead(body: { seen?: Array<{ id: string; at: string }>; ids?: string[]; all?: boolean }): void {
  void fetch(`${API}/read`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(r => (r.ok ? r.json() : null)).then(j => { if (j && Number.isFinite(Number(j.unread)) && Number(j.unread) !== unread) refreshSoon(200); })
    .catch(() => { /* the next poll re-syncs */ });
}

function closeMenu(): void {
  const root = document.getElementById('tn-notif');
  root?.classList.remove('open');
  root?.querySelector('#notif-btn')?.setAttribute('aria-expanded', 'false');
}
function openTask(n: Notif): void {
  closeMenu();
  if (n.deleted || !n.task_id) return;
  try { sessionStorage.setItem('tk_focus_task', n.task_id); } catch { /* private mode */ }
  document.dispatchEvent(new CustomEvent('tasks:focus', { detail: n.task_id }));   // TasksPage on screen → opens now
  document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'tasks' }));    // elsewhere → its render() takes the hand-off
}

// TopNav.bindEvents() — every nav render builds a fresh bell.
export function bindNotifBell(): void {
  const root = document.getElementById('tn-notif');
  if (!root || root.dataset.bound === '1') return;
  root.dataset.bound = '1';
  const btn = root.querySelector<HTMLElement>('#notif-btn');
  btn?.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = root.classList.contains('open');
    document.querySelectorAll('.tn-drop.open').forEach(d => d.classList.remove('open'));
    if (!wasOpen) {
      root.classList.add('open');
      btn.setAttribute('aria-expanded', 'true');
      if (!lastOkAt || Date.now() - lastOkAt > 15000) void refreshNotifications();
    } else btn.setAttribute('aria-expanded', 'false');
  });
  root.querySelector<HTMLElement>('.tn-notif-menu')?.addEventListener('click', (e) => {
    const el = e.target as HTMLElement;
    if (el.closest('[data-notif-all]')) {
      if (!unread) return;
      const seen = items.filter(i => !i.read).map(i => ({ id: i.id, at: i.at }));
      items = items.map(i => ({ ...i, read: true }));
      setItems(items, 0);
      markRead({ seen });
      return;
    }
    if (el.closest('[data-notif-open-tasks]')) {
      closeMenu();
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'tasks' }));
      return;
    }
    const row = el.closest<HTMLElement>('[data-notif-id]');
    if (!row) return;
    const n = items.find(i => i.id === row.dataset.notifId);
    if (!n) return;
    if (!n.read) {
      items = items.map(i => (i.id === n.id ? { ...i, read: true } : i));
      setItems(items, Math.max(0, unread - 1));
      markRead({ seen: [{ id: n.id, at: n.at }] });
    }
    openTask(n);
  });
  if (!docBound) {
    docBound = true;
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && document.getElementById('tn-notif')?.classList.contains('open')) closeMenu(); });
  }
  // A landing that skips App's mount line (deep link) still gets the poll.
  if (!started && !startTimer && bellAllowed()) startTimer = window.setTimeout(() => { startTimer = undefined; mountNotifBell(); }, 4000);
}

// App, after the first page load (idempotent).
export function mountNotifBell(): void {
  if (started || !bellAllowed()) return;
  started = true;
  if (startTimer) { window.clearTimeout(startTimer); startTimer = undefined; }
  void refreshNotifications();
  pollTimer = window.setInterval(() => { if (!document.hidden) void refreshNotifications(); }, POLL_MS);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) void refreshNotifications(); });
  document.addEventListener('tasks:changed', () => refreshSoon());
}
export function _bellStateForTests(): { items: Notif[]; unread: number; started: boolean; poll: boolean } {
  return { items, unread, started, poll: pollTimer !== undefined };
}

function ensureStyles(): void {
  if (document.getElementById('tn-notif-css')) return;
  const st = document.createElement('style');
  st.id = 'tn-notif-css';
  st.textContent = `
    .tn-notif-btn { position:relative; display:inline-flex; align-items:center; justify-content:center; min-width:36px; min-height:36px; padding:0 8px; }
    .tn-notif-n { position:absolute; top:2px; right:1px; min-width:17px; height:17px; padding:0 4px; border-radius:var(--radius-pill); background:var(--danger); color:#fff; font-size:10px; font-weight:700; line-height:17px; text-align:center; box-shadow:0 0 0 2px var(--bg); font-variant-numeric:tabular-nums; }
    .tn-notif-n[hidden] { display:none; }
    .tn-drop.tn-notif:not(.open):hover .tn-menu { display:none; }   /* click-to-open only (the nav menus also open on hover) */
    .tn-notif-menu { width:min(380px, calc(100vw - 24px)); padding:0; overflow:hidden; }
    .tn-notif-head, .tn-notif-foot { display:flex; align-items:center; gap:8px; padding:6px 8px 6px 14px; }
    .tn-notif-head { border-bottom:1px solid var(--border-soft); }
    .tn-notif-foot { border-top:1px solid var(--border-soft); justify-content:flex-end; }
    .tn-notif-h { font-size:13px; font-weight:700; color:var(--text); flex:1; }
    .tn-notif-hn { font-size:11px; font-weight:600; color:var(--accent); margin-left:4px; }
    .tn-notif-act { border:none; background:transparent; color:var(--accent); font:inherit; font-size:12px; font-weight:600; padding:0 10px; min-height:36px; border-radius:var(--radius-btn); cursor:pointer; }
    .tn-notif-act:hover { background:var(--accent-soft); }
    .tn-notif-act[disabled] { color:var(--text-faint); cursor:default; background:transparent; }
    .tn-notif-list { max-height:min(420px, calc(100vh - 170px)); overflow-y:auto; padding:4px; }
    .tn-notif-item { display:flex; gap:10px; align-items:flex-start; width:100%; text-align:left; border:none; background:transparent; padding:10px 10px 10px 8px; border-radius:var(--radius-sm); cursor:pointer; font:inherit; color:var(--text); }
    .tn-notif-item:hover { background:var(--bg-subtle); }
    .tn-notif-item.unread { background:var(--accent-soft); }
    .tn-notif-item.unread:hover { background:var(--info-soft); }
    .tn-notif-dot { flex:none; width:8px; height:8px; margin-top:6px; border-radius:50%; background:transparent; }
    .tn-notif-item.unread .tn-notif-dot { background:var(--accent); }
    .tn-notif-body { flex:1; min-width:0; display:flex; flex-direction:column; gap:3px; }
    .tn-notif-top { display:flex; align-items:center; gap:8px; min-width:0; }
    .tn-notif-title { flex:1; min-width:0; font-size:12.5px; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .tn-notif-text { font-size:12px; color:var(--text); line-height:1.45; overflow-wrap:anywhere; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
    .tn-notif-meta { font-size:11px; color:var(--text-faint); }
    .tn-notif-empty { padding:22px 16px; font-size:12px; color:var(--text-muted); text-align:center; line-height:1.5; }
    @media (max-width: 760px) {
      /* The bell's 42px must not push the account chip onto a second nav row
         on a phone (390px): tighten the right cluster a little. These rules
         only exist while a bell renders (owners/accounting keep the old nav). */
      .topnav { gap:4px; }
      .topnav .tn-month { font-size:11px; padding:4px 6px; }
      .topnav .tn-user { gap:5px; padding:2px; }
      .tn-drop-right.tn-notif .tn-notif-menu { position:fixed; left:12px; right:12px; top:56px; width:auto; }
      .tn-notif-list { max-height:calc(100vh - 170px); }
    }`;
  document.head.appendChild(st);
}
